// Données envoyées au service de paiement depuis l'écran de don v2.
//
// L'écran de don ne parle pas à la banque : il construit exactement le même objet que
// la page de paiement commune (components/paiement, jamais modifiée) et le poste à la
// même adresse. Ce module est PUR — il se teste sans navigateur — parce qu'une erreur
// ici se paie en dons perdus : montant, devise, abonnement, reçu fiscal, rattachement
// à la campagne et à l'équipe.
//
// Règles reprises de la page commune (components/paiement/index.js onSubmit) :
//   – `montant` est en unités (50 = 50 €), jamais en centimes ;
//   – un don étalé devient un abonnement : mensualité arrondie au supérieur ;
//   – un don mensuel est un abonnement dont la durée 0 signifie « sans fin » ;
//   – `facturation` est un objet plat, complet, jamais partiel ;
//   – le bloc `campagne` est renvoyé tel quel : le serveur impose lui-même l'identité
//     de la campagne, le doublement et l'affichage.

import { getPurOrg } from "../../../../utils";

export const PAIEMENT_LINK_URL = "/paiements/offline/link/";

/** Mensualité réellement prélevée quand un don est étalé : arrondie à l'unité supérieure. */
export { installmentAmount as monthlyAmount } from "../../../../components/paiement/financial";
import { installmentAmount, feeQuote } from "../../../../components/paiement/financial";

/**
 * Fréquence du don, dans les termes du service de paiement.
 * @param {{amount:number, months:number, monthly:boolean}} donation
 */
export function frequency({ amount, months = 0, monthly = false }) {
    // Don mensuel récurrent : le montant saisi EST la mensualité ; 0 mois = sans fin.
    if (monthly) return { montant: amount, abo: true, nbMonth: Number(months) || 0 };
    // Don étalé : le total est découpé en mensualités.
    if (months > 1) return { montant: installmentAmount(amount, months), abo: true, nbMonth: months };
    return { montant: amount, abo: false, nbMonth: 0 };
}

/** Bloc du reçu fiscal : toujours plat et complet (un champ manquant casse les reçus). */
export function billing(values = {}) {
    const text = (value) => (typeof value === "string" ? value.trim() : "");
    const perso = values.perso !== false;

    const facturation = {
        perso,
        adresse: text(values.adresse),
        postal: text(values.postal),
        ville: text(values.ville),
        pays: text(values.pays),
    };

    if (perso) {
        facturation.nom = text(values.nom);
        facturation.prenom = text(values.prenom);
    } else {
        facturation.raison = text(values.raison);
        facturation.siren = text(values.siren);
    }

    if (text(values.etat)) facturation.etat = text(values.etat);
    if (text(values.social_id)) facturation.social_id = text(values.social_id);
    if (values.giftAid === true) facturation.giftAid = true;

    return facturation;
}

/**
 * Objet complet attendu par POST /paiements/offline/link/.
 *
 * @param {object} input
 *  - campagneData : campagne publique (id, label, assoc, config)
 *  - context      : contexte de la page (org, langValue)
 *  - donation     : choix faits sur la page — {amount, devise, months, monthly, campagne, returnUrl}
 *  - values       : champs saisis sur l'écran de don
 *  - platform     : moyen de paiement choisi (facultatif)
 *  - name         : libellé du paiement, traduit par l'écran (« Don pour … »)
 *  - source       : "WEB" | "APP", comme la page commune (resolveSource)
 */
export function buildCheckoutConfig({ campagneData, context, donation, values = {}, platform = null, name = null, source = null, contact = null }) {
    const org = context?.org || {};
    const { montant, abo, nbMonth } = frequency(donation);

    const config = {
        type: "CAMPAGNE",
        typeId: campagneData?.id || null,
        assoc: donation?.campagne?.assoc || campagneData?.assoc || 'MAIN',
        // Libellé repris tel quel de la page commune : il apparaît sur le checkout de la
        // banque, sur le reçu et dans les emails.
        name: name || `Don pour ${campagneData?.label || ""}`.trim(),
        montant,
        devise: donation?.devise || org.devise || "eur",
        cerfa: true,
        abo,
        nbMonth,
        ...(abo ? {paymentMode: donation?.monthly ? "MONTHLY" : "SPLIT"} : {}),
        ...(!donation?.monthly && donation?.months > 1 ? {divide: donation.months} : {}),
        ...(donation?.selectedProductId ? {selectedProductId: donation.selectedProductId} : {}),
        lang: context?.langValue || org.language || "fr",
        organisation: getPurOrg(org),
        returnUrl: donation?.returnUrl || null,
        campagne: { ...(donation?.campagne || {}) },
        // Donateur connecté à son espace de compte : le don rejoint sa fiche et son
        // historique. Le serveur ne l'accepte que si le contact appartient bien au
        // compte de la session (garde A7), d'où l'envoi par le relais authentifié.
        ...(contact ? { contact } : {}),
        commentaire: typeof values.commentaire === "string" && values.commentaire.trim() ? values.commentaire.trim() : null,
        info: {
            nom: typeof values.nom === "string" ? values.nom.trim() : "",
            prenom: typeof values.prenom === "string" ? values.prenom.trim() : "",
            email: typeof values.email === "string" ? values.email.trim() : "",
            tel: values.tel || null,
            homme: values.homme,
        },
        facturation: billing(values),
    };

    if (values.extra && typeof values.extra === "object") {
        config.info.extra = values.extra;
    }
    if (source === "APP" || source === "WEB") config.source = source;

    // Nom affiché sur le mur des dons ; vide = don anonyme.
    const displayName = typeof values.displayName === "string" ? values.displayName.trim() : "";
    if (displayName) config.campagne.displayName = displayName;
    else delete config.campagne.displayName;

    if (platform) config.platform = platform;
    const recurrentPlatform = org?.web?.["paiement.platform.recurrent"];
    if (abo && recurrentPlatform) {
        config.platform = recurrentPlatform;
    }
    if (config.platform === "AAC") {
        config.aacAccountNumber = values.aacAccountNumber || null;
    }

    return config;
}

/** Corps de la requête : le service attend la configuration en TEXTE, pas en objet. */
export function checkoutRequestBody(config) {
    return { config: JSON.stringify(config) };
}

/**
 * Frais bancaires que le donateur peut prendre à sa charge, calculés comme la page de
 * paiement commune : 0,25 fixe + 1,5 % (particulier) ou 2,5 % (entreprise) du total.
 * @returns {number} montant des frais, arrondi au centime
 */
export function bankFees(montant, perso = true) {
    const amount = Number(montant) || 0;
    if (amount <= 0) return 0;
    return feeQuote(amount, perso).fees;
}
